/**
 * Employee Web — `/me/directory` route (EW-19, Plan 05.2-11).
 *
 * Mounts <DirectoryPage>; breadcrumb label resolves via the `directory`
 * namespace (allowlisted in `BREADCRUMB_NAMESPACES` since Plan 05.2-01).
 *
 * Search-param schema validates the optional deep-link state — `?view=list`
 * jumps straight to the table; `?q=` / `?dept=` / `?location=` / `?page=`
 * round-trip through the URL so the back button restores filter state.
 */
import type { JSX } from "react";
import { z } from "zod";
import { createFileRoute } from "@tanstack/react-router";
import { DirectoryPage } from "@linkhr/employee-self-service";

const directorySearchSchema = z.object({
  view: z.enum(["grid", "list"]).optional(),
  q: z.string().optional(),
  page: z.number().int().nonnegative().optional(),
  dept: z.string().optional(),
  location: z.string().optional(),
});

export const Route = createFileRoute("/_auth/me/directory")({
  staticData: { breadcrumb: { labelKey: "directory:page.title" } },
  validateSearch: directorySearchSchema,
  component: MeDirectoryRoute,
});

function MeDirectoryRoute(): JSX.Element {
  const search = Route.useSearch();
  const props: React.ComponentProps<typeof DirectoryPage> = {};
  if (search.view !== undefined) props.initialView = search.view;
  if (search.q !== undefined) props.initialQ = search.q;
  if (search.page !== undefined) props.initialPage = search.page;
  if (search.dept !== undefined) props.initialDept = search.dept;
  if (search.location !== undefined) props.initialLocation = search.location;
  return <DirectoryPage {...props} />;
}
